import React from 'react';
import { KatanaPoint, KatanaTSP } from '@/types';
import { ReusableModal } from '../../modals/ReusableModal';
import { PointMode } from './types';

type PointSelectionModalProps = {
  isOpen: boolean;
  mode: PointMode | null;
  onClose: () => void;
  pointSearch: string;
  onPointSearchChange: (value: string) => void;
  onPrevPage: () => void;
  onNextPage: () => void;
  pageHasPrev: boolean;
  pageHasNext: boolean;
  pointResults: KatanaPoint[];
  pointPipelineId: number | undefined;
  onPointPipelineIdChange: (value: number | undefined) => void;
  pipelineOptions: KatanaTSP[];
  pipelineNameById: Record<number, string>;
  selectedReceiptIds: number[];
  selectedDeliveryIds: number[];
  onToggleReceiptPoint: (point: KatanaPoint) => void;
  onToggleDeliveryPoint: (point: KatanaPoint) => void;
};

export const PointSelectionModal: React.FC<PointSelectionModalProps> = ({
  isOpen,
  mode,
  onClose,
  pointSearch,
  onPointSearchChange,
  onPrevPage,
  onNextPage,
  pageHasPrev,
  pageHasNext,
  pointResults,
  pointPipelineId,
  onPointPipelineIdChange,
  pipelineOptions,
  pipelineNameById,
  selectedReceiptIds,
  selectedDeliveryIds,
  onToggleReceiptPoint,
  onToggleDeliveryPoint,
}) => {
  const isReceipt = mode === 'receipt';
  const selectedIds = isReceipt ? selectedReceiptIds : selectedDeliveryIds;

  return (
    <ReusableModal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      header={
        <span>
          {mode === 'receipt' ? 'Select Receipt Points' : 'Select Delivery Points'}
        </span>
      }
    >
      <div className="d-flex flex-column gap-2">
        <div className="row g-2">
          <div className="col-md-6">
            <label className="form-label mb-1">Search Points</label>
            <input
              className="form-control form-control-sm"
              type="text"
              value={pointSearch}
              onChange={(evt) => onPointSearchChange(evt.target.value)}
              placeholder="Search point name"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label mb-1">Pipeline</label>
            <select
              className="form-select form-select-sm"
              value={pointPipelineId ?? ''}
              onChange={(evt) => {
                const value = evt.target.value;
                onPointPipelineIdChange(value ? Number(value) : undefined);
              }}
            >
              <option value="">All Pipelines</option>
              {pipelineOptions.map((tsp) => (
                <option key={`point-modal-tsp-${tsp.id}`} value={tsp.id}>
                  {tsp.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center">
          <small className="text-muted">
            {isReceipt
              ? `Selected receipt points: ${selectedIds.length}`
              : `Selected delivery points: ${selectedIds.length}`}
          </small>
          <div className="btn-group">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={!pageHasPrev}
              onClick={onPrevPage}
            >
              Prev
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={!pageHasNext}
              onClick={onNextPage}
            >
              Next
            </button>
          </div>
        </div>

        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          {pointResults.map((point) => {
            const checked = selectedIds.includes(point.id);
            const pipelineName = point.katana_tsp_id
              ? pipelineNameById[point.katana_tsp_id] || `TSP ${point.katana_tsp_id}`
              : null;

            return (
              <label
                key={`point-modal-${point.id}`}
                className="d-block small border rounded p-2 mb-1"
              >
                <input
                  type="checkbox"
                  className="form-check-input me-2"
                  checked={checked}
                  onChange={() => {
                    if (isReceipt) {
                      onToggleReceiptPoint(point);
                      return;
                    }
                    onToggleDeliveryPoint(point);
                  }}
                />
                <span className="fw-semibold">{point.name || 'Unnamed point'}</span>
                {pipelineName && <small className="text-muted ms-2">{pipelineName}</small>}
              </label>
            );
          })}
        </div>

        {pointResults.length === 0 && (
          <div className="text-muted">No points found for current filters.</div>
        )}

        <div className="d-flex justify-content-end">
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </ReusableModal>
  );
};
